const IMG_BASE_PATH = "/images/svg/";

export const IMPORT_SOURCES = Object.freeze({
    JIRA: "Jira",
    TRELLO: "Trello",
    ASANA: "Asana",
    SLACK: "Slack",
    CUSTOM: "Custom",
    PROSPUS: "Prospus",
    JIRA_AUTOMATION: "Jira-Automation",
    ASANA_AUTOMATION: "Asana-Automation",
    CLICKUP: "ClickUp",
    CLICKUP_AUTOMATION: "ClickUp-Automation",
});

export const AUTOMATION_SOURCES = Object.freeze([
    IMPORT_SOURCES.JIRA_AUTOMATION,
    IMPORT_SOURCES.ASANA_AUTOMATION,
    IMPORT_SOURCES.CLICKUP_AUTOMATION,
]);

export const IMPORT_TYPES = Object.freeze({
    WORK_PANEL_IMPORT_TRELLO: "work-panel-import-trello",
    WORK_PANEL_IMPORT_CUSTOM: "work-panel-import-custom",
    WORK_PANEL_IMPORT_JIRA: "work-panel-import-jira",
    WORK_PANEL_IMPORT_JIRA_JSON: "work-panel-import-jira-json",
    WORK_PANEL_IMPORT_SLACK: "work-panel-import-slack",
    WORK_PANEL_IMPORT_ASANA: "work-panel-import-asana",
    WORK_PANEL_IMPORT_ASANA_JSON: "work-panel-import-asana-json",
    WORK_PANEL_IMPORT_PROSPUS: "work-panel-import-prospus",
    WORK_PANEL_IMPORT_CLICKUP: "work-panel-import-clickup",
    WORK_PANEL_IMPORT_CLICKUP_JSON: "work-panel-import-clickup-json",
    /** Client-side saved import wizard draft (Operations tray, not a server job). */
    WORK_PANEL_IMPORT_DRAFT: "work-panel-import-draft",
});

export const FLIPPED_STRUCTURE_SOURCES = Object.freeze([
    IMPORT_SOURCES.TRELLO,
    IMPORT_SOURCES.JIRA,
    IMPORT_SOURCES.CUSTOM,
    IMPORT_SOURCES.ASANA,
    IMPORT_SOURCES.PROSPUS,
    IMPORT_SOURCES.CLICKUP,
]);

export const MULTI_SELECT_FIELDS = Object.freeze([
    "item.note",
    "item.comments",
    "item.participants",
]);

export const MANDATORY_FIELDS_BY_SOURCE = Object.freeze({
    [IMPORT_SOURCES.TRELLO]: ["board.name", "item_category.name", "item.name"],
    [IMPORT_SOURCES.JIRA]: ["board.name", "item_category.name", "item.name"],
    [IMPORT_SOURCES.ASANA]: ["board.name", "item_category.name", "item.name"],
    [IMPORT_SOURCES.SLACK]: ["item.name"],
    [IMPORT_SOURCES.CUSTOM]: [
        "workspace.name",
        "workspace_category.name",
        "board_category.name",
        "board.name",
        "item_category.name",
        "item.name",
    ],
    [IMPORT_SOURCES.PROSPUS]: ["item.participants"],
    [IMPORT_SOURCES.CLICKUP]: [
        "workspace.name",
        "board_category.name",
        "board.name",
        "item_category.name",
        "item.name",
    ],
});

export const ALLOWED_FILE_EXTENSIONS = Object.freeze({
    [IMPORT_SOURCES.TRELLO]: ["csv", "json", "zip"],
    [IMPORT_SOURCES.CUSTOM]: ["csv"],
    [IMPORT_SOURCES.JIRA]: ["csv", "json", "zip"],
    [IMPORT_SOURCES.JIRA_AUTOMATION]: ["json"],
    [IMPORT_SOURCES.SLACK]: ["csv", "json", "zip"],
    [IMPORT_SOURCES.ASANA]: ["csv", "json"],
    [IMPORT_SOURCES.ASANA_AUTOMATION]: ["json"],
    [IMPORT_SOURCES.PROSPUS]: ["zip", "csv"],
    [IMPORT_SOURCES.CLICKUP]: ["csv"],
    [IMPORT_SOURCES.CLICKUP_AUTOMATION]: ["json"],
});

export const SUPPORTED_UPLOAD_FORMATS = Object.freeze({
    [IMPORT_SOURCES.TRELLO]: ["csv", "json", "zip"],
    [IMPORT_SOURCES.CUSTOM]: ["csv"],
    [IMPORT_SOURCES.JIRA]: ["csv"],
    [IMPORT_SOURCES.JIRA_AUTOMATION]: ["json"],
    [IMPORT_SOURCES.SLACK]: ["zip"],
    [IMPORT_SOURCES.ASANA]: ["csv"],
    [IMPORT_SOURCES.ASANA_AUTOMATION]: ["json"],
    [IMPORT_SOURCES.PROSPUS]: ["zip", "csv"],
    [IMPORT_SOURCES.CLICKUP]: ["csv"],
    [IMPORT_SOURCES.CLICKUP_AUTOMATION]: ["json"],
});

export const KAAMFU_STATUS_OPTIONS = Object.freeze([
    { label: "Open", value: "open" },
    { label: "Closed", value: "closed" },
    { label: "In Progress", value: "in_progress" },
    { label: "Not Started", value: "not_started" },
]);

export const PROGRESS_STATUS = Object.freeze({
    PENDING: "pending",
    QUEUED: "queued",
    PROCESSING: "processing",
    STARTED: "started",
    COMPLETED: "completed",
    ERROR: "error",
});

export const PROGRESS_STATUS_COLOR = Object.freeze({
    [PROGRESS_STATUS.PENDING]: "warning",
    [PROGRESS_STATUS.QUEUED]: "warning",
    [PROGRESS_STATUS.PROCESSING]: "accent",
    [PROGRESS_STATUS.STARTED]: "accent",
    [PROGRESS_STATUS.COMPLETED]: "success",
    [PROGRESS_STATUS.ERROR]: "error",
});

export const PROGRESS_STATUS_TEXT = Object.freeze({
    [PROGRESS_STATUS.PENDING]: "Pending",
    [PROGRESS_STATUS.QUEUED]: "Queued",
    [PROGRESS_STATUS.PROCESSING]: "Processing",
    [PROGRESS_STATUS.STARTED]: "Processing",
    [PROGRESS_STATUS.COMPLETED]: "Completed",
    [PROGRESS_STATUS.ERROR]: "Error",
});

export const IMPORT_TYPE_SOURCE_MAP = Object.freeze({
    [IMPORT_TYPES.WORK_PANEL_IMPORT_TRELLO]: "Trello",
    [IMPORT_TYPES.WORK_PANEL_IMPORT_CUSTOM]: "Excel",
    [IMPORT_TYPES.WORK_PANEL_IMPORT_JIRA]: "Jira",
    [IMPORT_TYPES.WORK_PANEL_IMPORT_JIRA_JSON]: "Jira",
    [IMPORT_TYPES.WORK_PANEL_IMPORT_SLACK]: "Slack",
    [IMPORT_TYPES.WORK_PANEL_IMPORT_ASANA]: "Asana",
    [IMPORT_TYPES.WORK_PANEL_IMPORT_ASANA_JSON]: "Asana",
    [IMPORT_TYPES.WORK_PANEL_IMPORT_PROSPUS]: "Kaamfu",
    [IMPORT_TYPES.WORK_PANEL_IMPORT_CLICKUP]: "ClickUp",
    [IMPORT_TYPES.WORK_PANEL_IMPORT_CLICKUP_JSON]: "ClickUp",
});

export const SOURCE_CARD_OPTIONS = Object.freeze([
    {
        label: "Kaamfu",
        value: IMPORT_SOURCES.PROSPUS,
        logo: `${IMG_BASE_PATH}Kaamfu-Prospus.svg`,
    },
    {
        label: "Jira",
        value: IMPORT_SOURCES.JIRA,
        logo: `${IMG_BASE_PATH}jira.svg`,
    },
    {
        label: "Trello",
        value: IMPORT_SOURCES.TRELLO,
        logo: `${IMG_BASE_PATH}trello.svg`,
    },
    {
        label: "Asana",
        value: IMPORT_SOURCES.ASANA,
        logo: `${IMG_BASE_PATH}asana.svg`,
    },
    {
        label: "ClickUp",
        value: IMPORT_SOURCES.CLICKUP,
        logo: `${IMG_BASE_PATH}clickup.svg`,
    },
    {
        label: "Slack",
        value: IMPORT_SOURCES.SLACK,
        logo: `${IMG_BASE_PATH}slack.svg`,
    },
    {
        label: "Excel",
        value: IMPORT_SOURCES.CUSTOM,
        logo: `${IMG_BASE_PATH}ms-excel.svg`,
    },
]);

export const CUSTOM_SOURCE_TEMPLATE_COLUMNS = Object.freeze([
    // "Space_Group",
    // "Space",
    "Board_Group",
    "Board",
    // "Board_Start_Date",
    // "Board_End_Date",
    "Item_Group",
    "Item",
    "Item_Creator",
    "Item_Creation_Date",
    // "Item_Updated_At",
    "Closed_At",
    // "Assigner",
    "Assigner_Email",
    // "Assignee",
    "Assignee_Email",
    "Notes",
    "Comments",
    "Attachments",
    "Conversation",
    "Participants (separated by comma)",
]);

export const PROSPUS_TEMPLATE_COLUMNS = Object.freeze([
    "Task_File_Name",
    "Task_Title",
    "Board_Column_Name",
    "Board_Name",
    "Task_Column_Name",
    "Item_Notes",
    "Participants (separated by comma)",
    "Assigner",
    "Assignee",
]);

// export const CLICKUP_TEMPLATE_COLUMNS = Object.freeze([
//     "Task ID",
//     "Task Name",
//     "Task Content",
//     "Assignees",
//     "Tags",
//     "Priority",
//     "List Name",
//     "Folder Name/Path",
//     "Space Name",
//     "Time Estimated",
//     "Time Estimated Text",
//     "Checklists",
//     "Comments",
// ]);

export const IMPORT_STEPS = Object.freeze({
    UPLOAD_DATA: 1,
    MAP_TASKS_CHAT: 2,
    SELECT_BOARDS: 2, // Same step number for automation
    PREVIEW_IMPORT: 3,
    INVITE_MAP_USERS: 4,
    IMPORT_DATA: 5,
});

export const STEPPER_ITEMS_DEFAULT = Object.freeze([
    "Upload Data",
    "Map Tasks & Chat",
    "Preview Import",
    "Invite or Map Users",
    "Import Data",
]);

export function isSourceValue(selectedSource, targetValue) {
    const val = selectedSource?.value || selectedSource;
    return (
        val === targetValue ||
        String(val || "").toLowerCase() === targetValue.toLowerCase()
    );
}

export const IMPORT_TARGET_TYPES = Object.freeze({
    BOARD: "board",
    ITEM_GROUP: "item-group",
});

export function isKaamfuImportSource(selectedSource) {
    return isSourceValue(selectedSource, IMPORT_SOURCES.PROSPUS);
}

export function supportsDuplicateItemNameOption(selectedSource) {
    return (
        isSourceValue(selectedSource, IMPORT_SOURCES.TRELLO) ||
        isKaamfuImportSource(selectedSource) ||
        isSourceValue(selectedSource, IMPORT_SOURCES.JIRA) ||
        isSourceValue(selectedSource, IMPORT_SOURCES.ASANA) ||
        isSourceValue(selectedSource, IMPORT_SOURCES.CLICKUP) ||
        isCustomSource(selectedSource) ||
        isSlackSource(selectedSource)
    );
}

export function hasDuplicateItemNames(validationOrSummary) {
    return Boolean(validationOrSummary?.has_duplicate_item_names);
}

export const DUPLICATE_ITEM_NAME_ACTIONS = Object.freeze({
    FIX_AND_REUPLOAD: "fix_and_reupload",
    INCLUDE: "include",
    EXCLUDE: "exclude",
});

export const DEFAULT_DUPLICATE_ITEM_NAME_ACTION =
    DUPLICATE_ITEM_NAME_ACTIONS.FIX_AND_REUPLOAD;

export const DUPLICATE_ITEM_NAME_ACTION_OPTIONS = Object.freeze([
    {
        value: DUPLICATE_ITEM_NAME_ACTIONS.FIX_AND_REUPLOAD,
        label: "I will fix the duplicates in the CSV and re-upload.",
    },
    {
        value: DUPLICATE_ITEM_NAME_ACTIONS.INCLUDE,
        label: "Include duplicates - create as many items as there are, even if they are duplicates.",
    },
    {
        value: DUPLICATE_ITEM_NAME_ACTIONS.EXCLUDE,
        label: "Exclude duplicates - when there are duplicate items, just create one item, and drop all other duplicates.",
    },
]);

function readNumericCount(value) {
    if (value == null || value === "") {
        return null;
    }

    const count = Number(value);

    return Number.isNaN(count) ? null : count;
}

export function isDuplicateItemNameOccurrenceSummary(message) {
    return (
        typeof message === "string" &&
        message.startsWith("Duplicate item names found:")
    );
}

export function getPreviewDuplicateWarnings(warnings) {
    if (!Array.isArray(warnings)) {
        return [];
    }

    return warnings.filter(
        (warning) => !isDuplicateItemNameOccurrenceSummary(warning),
    );
}

export function getDuplicateItemNamesCount(validationOrSummary) {
    return (
        readNumericCount(validationOrSummary?.data_quality?.duplicate_item_names) ??
        readNumericCount(validationOrSummary?.duplicate_item_names)
    );
}

export function allowsDuplicateItemNameImport(action) {
    return action === DUPLICATE_ITEM_NAME_ACTIONS.INCLUDE;
}

export function resolveDuplicateItemNameAction({
    action,
    allowDuplicateItemName,
} = {}) {
    if (
        Object.values(DUPLICATE_ITEM_NAME_ACTIONS).includes(action)
    ) {
        return action;
    }

    if (allowDuplicateItemName === true) {
        return DUPLICATE_ITEM_NAME_ACTIONS.INCLUDE;
    }

    if (allowDuplicateItemName === false) {
        return DUPLICATE_ITEM_NAME_ACTIONS.EXCLUDE;
    }

    return DEFAULT_DUPLICATE_ITEM_NAME_ACTION;
}

export function shouldShowImportProblemsDetailSection({
    errorCount = 0,
    problemsCount = null,
} = {}) {
    return Number(errorCount) > 0 || Number(problemsCount) > 0;
}

export function shouldShowImportDuplicateDetailSection({
    hasDuplicateOption = false,
    duplicateItemNamesCount = null,
} = {}) {
    return Boolean(hasDuplicateOption) || Number(duplicateItemNamesCount) > 0;
}

export function shouldDisableProceedForDuplicateAction({
    hasErrors = false,
    hasDuplicateItemNames: hasDuplicates = false,
    duplicateItemNameAction,
} = {}) {
    if (hasErrors) {
        return true;
    }

    return (
        hasDuplicates &&
        duplicateItemNameAction === DUPLICATE_ITEM_NAME_ACTIONS.FIX_AND_REUPLOAD
    );
}

/**
 * Preview / import count after the user chooses whether to keep duplicate names.
 * Unique count keeps the first occurrence of each name.
 */
export function getDuplicateAwareItemsToImportCount({
    itemsCount,
    uniqueItemsCount,
    hasDuplicateItemNames: hasDuplicates = false,
    allowDuplicateItemName = false,
    duplicateItemNameAction = null,
} = {}) {
    const total = Number(itemsCount) || 0;
    const includeDuplicates =
        duplicateItemNameAction != null
            ? allowsDuplicateItemNameImport(duplicateItemNameAction)
            : allowDuplicateItemName;

    if (!hasDuplicates || includeDuplicates) {
        return total;
    }

    const unique = Number(uniqueItemsCount);
    if (Number.isNaN(unique) || uniqueItemsCount == null) {
        return total;
    }

    return unique;
}

export function buildKaamfuImportTarget({
    type,
    id,
    name,
    boardId = null,
    itemCategoryId = null,
    workspaceId = null,
    workspaceCategoryId = null,
} = {}) {
    return {
        type: type ?? null,
        id: id ?? null,
        name: name ?? "",
        board_id: boardId ?? null,
        item_category_id: itemCategoryId ?? null,
        workspace_id: workspaceId ?? null,
        workspace_category_id: workspaceCategoryId ?? null,
    };
}

export function isKaamfuPanelImportTarget(target) {
    return (
        target?.type === IMPORT_TARGET_TYPES.BOARD ||
        target?.type === IMPORT_TARGET_TYPES.ITEM_GROUP
    );
}

/**
 * Board / item-group Kaamfu imports land in a single existing board,
 * so preview and progress should not use the CSV's unique board count.
 */
export function getKaamfuImportBoardsToImportCount({
    boardsCount,
    selectedSource = null,
    importTarget = null,
} = {}) {
    const count = Number(boardsCount) || 0;

    if (
        count > 0 &&
        isKaamfuImportSource(selectedSource) &&
        isKaamfuPanelImportTarget(importTarget)
    ) {
        return 1;
    }

    return count;
}

/**
 * Board / item-group Kaamfu imports land in an existing board group,
 * so preview and progress should not count CSV board groups as imported.
 */
export function getKaamfuImportBoardGroupsToImportCount({
    boardGroupsCount,
    selectedSource = null,
    importTarget = null,
} = {}) {
    if (
        isKaamfuImportSource(selectedSource) &&
        isKaamfuPanelImportTarget(importTarget)
    ) {
        return 0;
    }

    return Number(boardGroupsCount) || 0;
}

export function shouldExitKaamfuPanelImportOnClick({
    importWorkspaceMode = false,
    importKaamfuSourceOnly = false,
    importTarget = null,
} = {}) {
    return Boolean(
        importWorkspaceMode &&
            importKaamfuSourceOnly &&
            isKaamfuPanelImportTarget(importTarget),
    );
}

/**
 * Whether a shrinked-panel double-click should expand its default panel.
 * Evaluate against the pre-click import state: a skipped leave-import
 * confirmation clears import_workspace_mode immediately, so a post-click
 * check would expand and overwrite the exit callback's destination.
 */
export function shouldOpenDefaultPanelOnDblClick({
    importWorkspaceMode = false,
    importKaamfuSourceOnly = false,
    importTarget = null,
} = {}) {
    if (
        shouldExitKaamfuPanelImportOnClick({
            importWorkspaceMode,
            importKaamfuSourceOnly,
            importTarget,
        })
    ) {
        return false;
    }

    return !importWorkspaceMode;
}

/**
 * Whether a shrinked-boards double-click should expand boards after
 * leave-import confirmation. False when a Kaamfu panel-import exit
 * (this event or the preceding click of the same double-click) already
 * owns the items destination — including after skipped confirmation
 * has already cleared import_workspace_mode.
 */
export function shouldOpenBoardsPanelOnShrinkedDblClick({
    importWorkspaceMode = false,
    importKaamfuSourceOnly = false,
    importTarget = null,
    precedingClickExitedKaamfuPanelImport = false,
} = {}) {
    if (precedingClickExitedKaamfuPanelImport) {
        return false;
    }

    return !shouldExitKaamfuPanelImportOnClick({
        importWorkspaceMode,
        importKaamfuSourceOnly,
        importTarget,
    });
}

export function getPanelImportPermissionResource(type) {
    return type === IMPORT_TARGET_TYPES.ITEM_GROUP ? "item-groups" : "boards";
}

export function getPanelImportDeniedMessage(type) {
    return type === IMPORT_TARGET_TYPES.ITEM_GROUP
        ? "You don't have permission to import into this item group."
        : "You don't have permission to import into this board.";
}

export function isImportSourceEnabled(sourceValue, kaamfuOnly = false) {
    if (!kaamfuOnly) {
        return true;
    }

    return isKaamfuImportSource(sourceValue);
}

export function getDisabledImportSourceTooltip(sourceName) {
    const name = String(sourceName ?? "").trim();
    if (!name) {
        return "";
    }

    return `To import from ${name}, go to Spaces under Work, choose any Space Group, click +, and select Import Workspace.`;
}

export function getImportTargetProgressMeta(importTarget = null) {
    if (!isKaamfuPanelImportTarget(importTarget)) {
        return {};
    }

    return {
        ...(importTarget.board_id ? { board_id: importTarget.board_id } : {}),
        ...(importTarget.item_category_id
            ? { item_category_id: importTarget.item_category_id }
            : {}),
        ...(importTarget.workspace_id
            ? { workspace_id: importTarget.workspace_id }
            : {}),
        ...(importTarget.workspace_category_id
            ? { workspace_category_id: importTarget.workspace_category_id }
            : {}),
    };
}

/**
 * True only when the live wizard target is the same board / item-group as the
 * clicked job. Space-level jobs (no board_id / item_category_id in meta) never
 * match, so an in-progress panel import cannot steal that destination.
 */
export function doesLiveImportTargetMatchJob(importTarget, meta = {}) {
    const itemCategoryId = meta?.item_category_id ?? null;
    const boardId = meta?.board_id ?? null;

    if (!itemCategoryId && !boardId) {
        return false;
    }

    const jobTargetType = itemCategoryId
        ? IMPORT_TARGET_TYPES.ITEM_GROUP
        : IMPORT_TARGET_TYPES.BOARD;
    const jobTargetId = itemCategoryId ?? boardId;

    return (
        isKaamfuPanelImportTarget(importTarget) &&
        importTarget.type === jobTargetType &&
        String(importTarget.id) === String(jobTargetId)
    );
}

/**
 * Which work-panel host should show Import Data for an Operations recent entry.
 * Uses the clicked job's meta, not the live import context: board / item-group
 * Kaamfu imports live in the items panel; space imports stay in spaces.
 */
export function getImportProgressPanelDestination({
    progress = null,
    type = null,
    importTarget = null,
} = {}) {
    const progressType = type ?? progress?.type ?? null;
    const meta = progress?.meta ?? {};

    if (progressType === IMPORT_TYPES.WORK_PANEL_IMPORT_DRAFT) {
        const draftState = meta.draft_state ?? {};
        if (draftState.kaamfuSourceOnly) {
            return {
                panel: "items",
                importTarget: isKaamfuPanelImportTarget(draftState.importTarget)
                    ? draftState.importTarget
                    : null,
            };
        }

        return { panel: "spaces", importTarget: null };
    }

    const itemCategoryId = meta.item_category_id ?? null;
    const boardId = meta.board_id ?? null;

    if (!itemCategoryId && !boardId) {
        return { panel: "spaces", importTarget: null };
    }

    const jobTargetType = itemCategoryId
        ? IMPORT_TARGET_TYPES.ITEM_GROUP
        : IMPORT_TARGET_TYPES.BOARD;
    const jobTargetId = itemCategoryId ?? boardId;
    const resolvedTarget = doesLiveImportTargetMatchJob(importTarget, meta)
        ? importTarget
        : buildKaamfuImportTarget({
              type: jobTargetType,
              id: jobTargetId,
              name: "",
              boardId,
              itemCategoryId,
              workspaceId: meta.workspace_id ?? null,
              workspaceCategoryId: meta.workspace_category_id ?? null,
          });

    return {
        panel: "items",
        importTarget: resolvedTarget,
    };
}

export function openImportProgressDestinationPanel(
    workspaceStore,
    destination,
) {
    if (destination?.panel === "items") {
        workspaceStore.setImportKaamfuSourceOnly(true);
        if (destination.importTarget) {
            workspaceStore.setImportTarget(destination.importTarget);
        }
        workspaceStore.openItemsPanel();
        return;
    }

    workspaceStore.setImportKaamfuSourceOnly(false);
    workspaceStore.setImportTarget(null);
    workspaceStore.openSpacesPanel();
}

/**
 * Board-level imports should open the board's items view (tasks)
 * instead of locating the board in the boards panel.
 */
export function shouldOpenImportedBoardItemsView(destination) {
    return (
        destination?.entityType === "board" &&
        Boolean(destination?.boardId) &&
        destination?.openItemsView === true
    );
}

export function isCompletedImportProgress(progressOrStatus) {
    const status =
        typeof progressOrStatus === "string"
            ? progressOrStatus
            : progressOrStatus?.status;

    return status === PROGRESS_STATUS.COMPLETED;
}

/**
 * Completed imports skip the wizard and open the imported destination.
 * In-progress and error jobs still open the Import Data step.
 */
export function shouldOpenImportedDestinationDirectly(progressOrStatus) {
    return isCompletedImportProgress(progressOrStatus);
}

/**
 * First stepper page when ImportWorkspace mounts. Must be Import Data when
 * opening a progress job so Upload Data never paints.
 */
export function resolveInitialImportStep({
    importState = null,
    importStep = null,
    persistedStep = null,
} = {}) {
    if (
        importState?.progressId ||
        importStep === IMPORT_STEPS.IMPORT_DATA
    ) {
        return IMPORT_STEPS.IMPORT_DATA;
    }

    if (persistedStep != null) {
        return persistedStep;
    }

    return IMPORT_STEPS.UPLOAD_DATA;
}

/**
 * Destination after a completed import. Job meta wins so Operations cannot
 * open another in-progress wizard's board or space. Live importTarget only
 * fills missing lineage when it is the same board / item-group as the job.
 */
export function getImportedWorkPanelNavigation(meta = {}, importTarget = null) {
    const useLiveTarget = doesLiveImportTargetMatchJob(importTarget, meta);
    const boardId =
        meta?.board_id ?? (useLiveTarget ? importTarget?.board_id : null) ?? null;
    const itemCategoryId =
        meta?.item_category_id ??
        (useLiveTarget ? importTarget?.item_category_id : null) ??
        null;
    const workspaceId =
        meta?.workspace_id ??
        (useLiveTarget ? importTarget?.workspace_id : null) ??
        null;
    const workspaceCategoryId =
        meta?.workspace_category_id ??
        (useLiveTarget ? importTarget?.workspace_category_id : null) ??
        null;

    if (itemCategoryId) {
        return {
            entityType: "item-group",
            entityId: itemCategoryId,
            boardId,
            workspaceId,
            workspaceCategoryId,
        };
    }

    if (boardId) {
        return {
            entityType: "board",
            entityId: boardId,
            boardId,
            workspaceId,
            workspaceCategoryId,
            openItemsView: true,
        };
    }

    if (workspaceId) {
        return {
            entityType: "workspace",
            entityId: workspaceId,
            boardId: null,
            workspaceId,
            workspaceCategoryId,
        };
    }

    return {
        entityType: "workspace-group",
        entityId: workspaceCategoryId,
        boardId: null,
        workspaceId: null,
        workspaceCategoryId,
    };
}

export function getKaamfuSourceOption() {
    return (
        SOURCE_CARD_OPTIONS.find(
            (option) => option.value === IMPORT_SOURCES.PROSPUS,
        ) ?? null
    );
}

/**
 * Resolve a source card (label, value, logo) from a selected source, display
 * label, internal value, or import job type. "Prospus" still maps to Kaamfu
 * after the UI rename.
 */
export function getSourceCardOption(source) {
    if (source == null) {
        return null;
    }

    if (typeof source === "object") {
        if (source.logo) {
            return source;
        }

        return getSourceCardOption(source.value ?? source.label);
    }

    const needle = String(source);
    const isKaamfuAlias =
        needle === IMPORT_SOURCES.PROSPUS ||
        needle === IMPORT_TYPES.WORK_PANEL_IMPORT_PROSPUS ||
        needle.toLowerCase() === "kaamfu" ||
        needle.toLowerCase() === "prospus";

    return (
        SOURCE_CARD_OPTIONS.find((option) => {
            if (option.value === needle || option.label === needle) {
                return true;
            }

            return isKaamfuAlias && option.value === IMPORT_SOURCES.PROSPUS;
        }) ?? null
    );
}

export function getSourceCardOptionForImportType(type) {
    if (!type) {
        return null;
    }

    return getSourceCardOption(IMPORT_TYPE_SOURCE_MAP[type] ?? type);
}

export function isAutomationSource(selectedSource) {
    const val = selectedSource?.value || selectedSource;
    return AUTOMATION_SOURCES.includes(val);
}

export function isCustomSource(selectedSource) {
    return isSourceValue(selectedSource, IMPORT_SOURCES.CUSTOM);
}

export function isSlackSource(selectedSource) {
    return isSourceValue(selectedSource, IMPORT_SOURCES.SLACK);
}

/**
 * Auto-mapped 1-on-1 fields are locked, matching Trello/Jira/ClickUp.
 * Item Notes stay editable so extra CSV columns can still be attached.
 */
export function isImportFieldMappingDisabled(field) {
    if (!field?.wasAutoMapped) {
        return false;
    }

    const constant = field.kaamfu_field_constant || "";

    if (constant === "item.note") {
        return false;
    }

    return true;
}

export function getKaamfuImportTemplateDownloadHref() {
    return `data:text/csv;charset=utf-8,${encodeURIComponent(
        `${PROSPUS_TEMPLATE_COLUMNS.join(",")}\n`,
    )}`;
}

export function linkKaamfuImportTemplateDownloadInMessage(message) {
    return String(message).replace(
        "here.",
        `<a href="${getKaamfuImportTemplateDownloadHref()}" download="kaamfu.csv" class="text-decoration-underline text-white">here</a>.`,
    );
}
